import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import axios from 'axios';
import { 
  Calendar, 
  User, 
  ArrowLeft, 
  Heart, 
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  Link2,
  Check,
  MessageSquare,
  Send
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [localLikes, setLocalLikes] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentData, setCommentData] = useState({ author: '', content: '' });
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchPost();
    // Check if user has already liked this post
    const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
    setLiked(likedPosts.includes(slug));

    // Close share menu on click outside
    const handleClickOutside = (e) => {
      if (showShareMenu && !e.target.closest('.share-container')) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [slug, showShareMenu]);

  useEffect(() => {
    if (post) {
      fetchComments();
    }
  }, [post]);

  const fetchPost = async () => {
    try {
      const res = await axios.get(`${API}/blog/${slug}`);
      setPost(res.data);
      setLocalLikes(res.data.likes || 0);
      
      // SEO
      if (res.data) {
        document.title = `${res.data.title} | Blog Viajes de Ensueño`;
        const description = res.data.excerpt?.substring(0, 160) || `Lee nuestro artículo sobre ${res.data.title}. Inspiración y consejos para tus próximos viajes de ensueño.`;
        document.querySelector('meta[name="description"]')?.setAttribute("content", description);
      }
    } catch (err) {
      console.error('Error fetching post:', err);
      toast.error('Artículo no encontrado');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/blog/${post.id}/comments`);
      setComments(res.data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentData.author || !commentData.content) {
      toast.error('Por favor, rellena todos los campos');
      return;
    }

    setSubmittingComment(true);
    try {
      await axios.post(`${API}/blog/${post.id}/comments`, commentData);
      toast.success('¡Comentario enviado con éxito!');
      setCommentData({ author: '', content: '' });
      fetchComments();
    } catch (err) {
      console.error('Error submitting comment:', err);
      toast.error('Error al enviar el comentario');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleLike = async () => {
    if (liked) {
      toast.info('Ya has dado "me gusta" a este artículo');
      return;
    }

    try {
      const res = await axios.post(`${API}/blog/${post.id}/like`);
      setLocalLikes(res.data.likes);
      setLiked(true);
      
      // Save to localStorage
      const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
      likedPosts.push(slug);
      localStorage.setItem('likedPosts', JSON.stringify(likedPosts));
      
      toast.success('¡Gracias por tu "me gusta"!');
    } catch (err) {
      console.error('Error liking post:', err);
      toast.error('Error al dar "me gusta"');
    }
  };

  const shareUrl = window.location.href;
  const shareTitle = post?.title || '';

  const handleShare = async (platform) => {
    if (platform === 'system' && navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
        // Track share
        axios.post(`${API}/blog/${post.id}/share`).catch(() => {});
        return;
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Error sharing:', err);
      }
    }

    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
      setShowShareMenu(false);
      // Track share
      axios.post(`${API}/blog/${post.id}/share`).catch(() => {});
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      toast.success('Enlace copiado al portapapeles');
      setTimeout(() => {
        setCopied(false);
        setShowShareMenu(false);
      }, 2000);
    }).catch(() => {
      toast.error('Error al copiar el enlace');
    });
  };

  const ShareMenu = ({ position = 'top-full mt-2' }) => (
    <div className={`absolute right-0 ${position} bg-white rounded-lg shadow-lg border border-slate-200 py-2 min-w-[200px] z-10`}>
      <button
        onClick={() => handleShare('facebook')}
        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 transition"
      >
        <Facebook className="w-4 h-4 text-blue-600" />
        <span>Facebook</span>
      </button>
      <button
        onClick={() => handleShare('twitter')}
        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 transition"
      >
        <Twitter className="w-4 h-4 text-sky-500" />
        <span>Twitter</span>
      </button>
      <button
        onClick={() => handleShare('linkedin')}
        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 transition"
      >
        <Linkedin className="w-4 h-4 text-blue-700" />
        <span>LinkedIn</span>
      </button>
      <button
        onClick={() => handleShare('whatsapp')}
        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 transition"
      >
        <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
        <span>WhatsApp</span>
      </button>
      <button
        onClick={() => handleShare('email')}
        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 transition"
      >
        <Mail className="w-4 h-4 text-slate-600" />
        <span>Email</span>
      </button>
      <div className="border-t border-slate-200 my-2"></div>
      <button
        onClick={copyToClipboard}
        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 transition"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-green-600">¡Copiado!</span>
          </>
        ) : (
          <>
            <Link2 className="w-4 h-4 text-slate-600" />
            <span>Copiar enlace</span>
          </>
        )}
      </button>
    </div>
  );

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "d 'de' MMMM, yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="container-custom py-12 text-center">
          <h1 className="text-2xl font-serif font-bold text-primary mb-4">
            Artículo no encontrado
          </h1>
          <Link to="/blog" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Volver al Blog
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={post.image_url}
          alt={post.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/1920x1080?text=Imagen+no+disponible';
          }}
        />
        <div className="hero-gradient absolute inset-0" />
      </div>

      {/* Content */}
      <article className="container-custom py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al Blog
          </Link>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-slate-200">
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.created_at)}</span>
            </div>

            {/* Like Button */}
            <button
              onClick={handleLike}
              disabled={liked}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                liked
                  ? 'bg-red-100 text-red-600 cursor-not-allowed'
                  : 'bg-slate-100 text-slate-700 hover:bg-red-100 hover:text-red-600'
              }`}
            >
              <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
              <span>{localLikes} Me gusta</span>
            </button>

            {/* Share Button */}
            <div className="relative share-container">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowShareMenu(!showShareMenu);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-primary hover:text-white transition"
              >
                <Share2 className="w-4 h-4" />
                <span>Compartir</span>
              </button>

              {/* Share Menu */}
              {showShareMenu && <ShareMenu position="top-full mt-2" />}
            </div>
          </div>

          {/* Excerpt */}
          <div className="text-xl text-slate-600 mb-8 font-medium leading-relaxed">
            {post.excerpt.replace(/<[^>]*>/g, '')}
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-primary prose-a:text-accent hover:prose-a:text-accent/80 prose-img:rounded-lg prose-img:w-full prose-img:h-auto"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Footer Actions */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleLike}
                  disabled={liked}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg transition font-medium ${
                    liked
                      ? 'bg-red-100 text-red-600 cursor-not-allowed'
                      : 'bg-slate-100 text-slate-700 hover:bg-red-600 hover:text-white'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
                  <span>{liked ? 'Te gusta este artículo' : 'Me gusta'}</span>
                </button>

                <div className="relative share-container">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowShareMenu(!showShareMenu);
                    }}
                    className="flex items-center gap-2 px-6 py-3 rounded-lg bg-slate-100 text-slate-700 hover:bg-primary hover:text-white transition font-medium"
                  >
                    <Share2 className="w-5 h-5" />
                    <span>Compartir</span>
                  </button>
                  {showShareMenu && <ShareMenu position="bottom-full mb-2" />}
                </div>
              </div>

              <Link
                to="/blog"
                className="btn-secondary inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver al Blog
              </Link>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 bg-gradient-to-r from-primary to-accent rounded-xl p-8 text-center text-white">
            <h3 className="text-2xl font-serif font-bold mb-4">
              ¿Te ha gustado este artículo?
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Descubre cómo podemos ayudarte a planificar tu próximo viaje de ensueño
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contacto" className="btn-primary bg-white text-primary hover:bg-slate-100">
                Contáctanos
              </Link>
              <Link to="/viajes" className="btn-secondary border-white text-white hover:bg-white/10">
                Ver Destinos
              </Link>
            </div>
          </div>

          {/* Comments Section */}
          <div className="mt-16 pt-12 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-8">
              <MessageSquare className="w-6 h-6 text-primary" />
              <h3 className="text-2xl font-serif font-bold text-primary">
                Comentarios ({comments.length})
              </h3>
            </div>

            {/* Comment Form */}
            <div className="bg-slate-50 rounded-xl p-6 mb-12">
              <h4 className="font-medium text-slate-800 mb-4">Deja tu comentario</h4>
              <form onSubmit={handleCommentSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600 mb-1.5 block">
                      Nombre
                    </label>
                    <Input
                      placeholder="Tu nombre"
                      value={commentData.author}
                      onChange={(e) => setCommentData({ ...commentData, author: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1.5 block">
                    Comentario
                  </label>
                  <Textarea
                    placeholder="Escribe aquí tu opinión o consulta..."
                    rows={4}
                    value={commentData.content}
                    onChange={(e) => setCommentData({ ...commentData, content: e.target.value })}
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full md:w-auto" 
                  disabled={submittingComment}
                >
                  {submittingComment ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Publicar comentario
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Comments List */}
            <div className="space-y-8">
              {comments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 italic">
                  Sé el primero en comentar este artículo.
                </p>
              ) : (
                comments.map((comment, index) => (
                  <div key={comment.id || index} className="flex gap-4 group">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                        {comment.author.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-bold text-slate-900">{comment.author}</h5>
                        <span className="text-xs text-slate-400">
                          {formatDate(comment.created_at)}
                        </span>
                      </div>
                      <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                        {comment.content}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}
